import axios from "axios"

import { getTemplateById } from "../config/globalBuildTemplates"
import {
  defaultFilterValuesAB,
  defaultFilterValuesCB,
  defaultRecordCountValuesAB,
  defaultRecordCountValuesCB,
} from "./defaultValuesFilterBuilders"
import { getDateRangeFromPreset } from "./datePresets"
import {
  buildBrandLoyaltyForMake,
  buildBrandLoyaltySql,
} from "./brandAffinityMap"
import {
  createSQLBrandLoyalty,
  createSQLDateSentence,
  createSQLListSentence,
} from "../components/AudienceCatBuilder/utils"

import { buildZipRadiusUrl, isZipCodeApiConfigured } from "./zipCodeApi"

const PREV_PURCH_DATE_COLUMN =
  "COALESCE(SAFE.PARSE_DATE('%m/%d/%Y',COALESCE(NULLIF(CAST(ContractDate AS STRING), ''), NULLIF(CAST(DeliveryDate AS STRING), ''), NULLIF(CAST(CustomerCreateDate AS STRING), ''), NULLIF(CAST(CustomerLastActivityDate AS STRING), '')) ), SAFE.PARSE_DATE('%Y-%m-%d',COALESCE(NULLIF(CAST(ContractDate AS STRING), ''), NULLIF(CAST(DeliveryDate AS STRING), ''), NULLIF(CAST(CustomerCreateDate AS STRING), ''), NULLIF(CAST(CustomerLastActivityDate AS STRING), '')) ) )"

const NEV_SER_DATE_COLUMN =
  "COALESCE(SAFE.PARSE_DATE('%m/%d/%Y',COALESCE(NULLIF(CAST(CloseDate AS STRING), ''), NULLIF(CAST(CustomerLastActivityDate AS STRING), ''), NULLIF(CAST(DeliveryDate AS STRING), '')) ), SAFE.PARSE_DATE('%Y-%m-%d',COALESCE(NULLIF(CAST(CloseDate AS STRING), ''), NULLIF(CAST(CustomerLastActivityDate AS STRING), ''), NULLIF(CAST(DeliveryDate AS STRING), '')) ) )"

const INFERRED_PURCHASE_2_TO_7 = [
  "2 Years to 3 Years",
  "3 Years to 4 Years",
  "4 or More Years",
]

const toDateSqlBody = (range) =>
  `CAST('${range[0]}' AS DATE FORMAT 'MM/DD/YYYY') AND CAST('${range[1]}' AS DATE FORMAT 'MM/DD/YYYY')`

export const fetchZipCodesForRadius = async (zipCode, radius) => {
  if (!zipCode || !radius) return []
  if (!isZipCodeApiConfigured()) {
    console.error(
      "REACT_APP_API_ZIP_CODES / REACT_APP_API_ZIP_CODES_DEAN are missing"
    )
    return []
  }

  const response = await axios.get(buildZipRadiusUrl(zipCode, radius))
  if (response.data?.error_code || !Array.isArray(response.data?.zip_codes)) {
    console.error(
      "Zip code API error:",
      response.data?.error_msg || response.data
    )
    return []
  }
  return response.data.zip_codes
    .map((entry) => String(entry.zip_code))
    .sort((a, b) => a - b)
}

const applyGeoFilter = async (
  template,
  filters,
  recordRequest,
  sql,
  dealerContext
) => {
  if (!template.geoRadius) return sql

  const zipCode = dealerContext.zipCode
  if (!zipCode) return sql

  const zipCodes = await fetchZipCodesForRadius(zipCode, template.geoRadius)
  if (!zipCodes.length) return sql

  const quoted = zipCodes.map((zip) => `'${zip}'`)
  const geoSql = createSQLListSentence(
    quoted,
    "zipCodes",
    "C.ZIP",
    filters,
    recordRequest,
    sql,
    false
  )

  filters.zipCodeCenter = zipCode
  filters.zipCodeRadius = String(template.geoRadius)
  filters.zipCodes = zipCodes
  filters.zipCodesCount = zipCodes.length
  recordRequest.zipCodes = zipCodes

  return { sql: geoSql }
}

const buildCustomerPreset = (template, dealerContext) => {
  const filters = {
    ...defaultFilterValuesCB,
    isGlobalTemplate: true,
    templateId: template.id,
  }
  const recordRequest = { ...defaultRecordCountValuesCB }
  let sqlSales = { sql: " AND 1=1" }
  let sqlService = { sql: " AND 1=0" }

  if (template.prevPurchDatePreset) {
    const range = getDateRangeFromPreset(template.prevPurchDatePreset)
    sqlSales = {
      sql: createSQLDateSentence(
        toDateSqlBody(range),
        "prevPurchDateRange",
        PREV_PURCH_DATE_COLUMN,
        filters,
        sqlSales,
        false
      ),
    }
    filters.prevPurchDateRange = range
  }

  if (template.nevSerPrevPurch) {
    filters.nevSerPrevPurch = true
  }

  if (template.nevSerDatePreset) {
    const range = getDateRangeFromPreset(template.nevSerDatePreset)
    sqlService = {
      sql: createSQLDateSentence(
        toDateSqlBody(range),
        "nevSerDateRange",
        NEV_SER_DATE_COLUMN,
        filters,
        sqlService,
        false
      ),
    }
    filters.nevSerDateRange = range
  }

  return {
    filterValues: filters,
    recordRequest,
    sqlQuery: null,
    sqlQuerySales: sqlSales,
    sqlQueryService: sqlService,
    builderType: "CUSTOMER",
    geoTag:
      dealerContext.zipCode && template.geoRadius
        ? `Zip Code ${dealerContext.zipCode} - Miles ${template.geoRadius}`
        : "",
  }
}

const buildAudiencePreset = async (template, dealerContext) => {
  const filters = {
    ...defaultFilterValuesAB,
    isGlobalTemplate: true,
    templateId: template.id,
  }
  const recordRequest = { ...defaultRecordCountValuesAB }
  let sql = { sql: " AND 1=1" }

  sql = await applyGeoFilter(
    template,
    filters,
    recordRequest,
    sql,
    dealerContext
  )

  if (template.brandOwners && dealerContext.primaryMake) {
    const brandLoyalty = buildBrandLoyaltyForMake(dealerContext.primaryMake)
    const loyaltySql = buildBrandLoyaltySql(brandLoyalty)
    sql = {
      sql: createSQLBrandLoyalty(
        loyaltySql,
        loyaltySql ? 1 : 0,
        "brandLoyalty",
        filters,
        sql
      ),
    }
    filters.brandLoyalty = brandLoyalty
    recordRequest.brandLoyalty = brandLoyalty
  }

  if (template.inferredPurchase2to7) {
    const quotedCodes = ["'E'", "'F'", "'G'"]
    sql = {
      sql: createSQLListSentence(
        quotedCodes,
        "inferredPurchasedDates",
        "A.INFERREDDATE",
        filters,
        recordRequest,
        sql,
        true
      ),
    }
    filters.inferredPurchasedDates = INFERRED_PURCHASE_2_TO_7
    recordRequest.inferredPurchasedDates = ["E", "F", "G"]
  }

  if (template.excludeSalesYears) {
    filters.excludeSales = String(template.excludeSalesYears)
    recordRequest.excludeSales = `Last ${template.excludeSalesYears} Years`
  }

  if (template.demographics) {
    filters.ageRange = { initialValue: 18, finalValue: 99 }
  }

  return {
    filterValues: filters,
    recordRequest,
    sqlQuery: sql,
    sqlQuerySales: null,
    sqlQueryService: null,
    builderType: "AUDIENCE",
    geoTag:
      dealerContext.zipCode && template.geoRadius
        ? `Zip Code ${dealerContext.zipCode} - Miles ${template.geoRadius}`
        : "",
  }
}

export const resolveGlobalBuildPreset = async (filterValues, dealerContext) => {
  if (!filterValues?.isGlobalTemplate || !filterValues?.templateId) {
    return null
  }

  const template = getTemplateById(filterValues.templateId)
  if (!template) return null

  if (template.builderType === "CUSTOMER") {
    return buildCustomerPreset(template, dealerContext)
  }

  return buildAudiencePreset(template, dealerContext)
}

export const isGlobalTemplatePreset = (filterValues) =>
  Boolean(filterValues?.isGlobalTemplate && filterValues?.templateId)
